import { useNavigate } from "react-router-dom";
import SectionLabel from "@/components/base/SectionLabel";
import { integratedBusinessData } from "@/mocks/aboutData";

export default function IntegratedBusinessSection() {
  const navigate = useNavigate();

  const handleClick = (href: string) => {
    if (href.startsWith("/#")) {
      navigate("/");
      setTimeout(() => {
        const el = document.querySelector(href.replace("/", ""));
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 300);
    } else if (href.startsWith("/")) {
      navigate(href);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (href.startsWith("#")) {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      window.open(href, "_blank", "noopener");
    }
  };

  return (
    <section id="two-business" className="py-16 md:py-24 bg-background-100">
      <div className="max-w-[1200px] mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="reveal text-center mb-6 md:mb-8">
          <SectionLabel label={integratedBusinessData.label} className="mb-3" />
          <h2 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-foreground-900">
            {integratedBusinessData.heading}
          </h2>
        </div>

        {/* Intro paragraph */}
        <div className="reveal max-w-2xl mx-auto mb-10 md:mb-14">
          <p className="text-sm md:text-base text-foreground-700 leading-relaxed text-center">
            {integratedBusinessData.intro}
          </p>
        </div>

        {/* Two business columns */}
        <div className="reveal max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 mb-8 md:mb-10">
            {/* Web production */}
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-7">
              <span className="inline-block text-[10px] md:text-xs font-medium text-secondary-500 tracking-[0.1em] uppercase mb-2">
                {integratedBusinessData.web.subtitle}
              </span>
              <h3 className="text-lg md:text-xl font-bold text-foreground-900 font-heading mb-4 md:mb-5">
                {integratedBusinessData.web.title}
              </h3>
              <ul className="space-y-2.5 mb-5 md:mb-6">
                {integratedBusinessData.web.items.map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2.5 text-sm text-foreground-700">
                    <i className="ri-checkbox-blank-circle-fill text-[6px] text-secondary-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <a
                href={integratedBusinessData.web.linkHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-secondary-600 hover:text-secondary-700 transition-colors cursor-pointer"
              >
                {integratedBusinessData.web.linkLabel}
                <i className="ri-external-link-line text-xs" />
              </a>
            </div>

            {/* DX support */}
            <div className="bg-primary-50/40 border border-primary-200/40 rounded-lg p-5 md:p-7">
              <span className="inline-block text-[10px] md:text-xs font-medium text-primary-500 tracking-[0.1em] uppercase mb-2">
                {integratedBusinessData.dx.subtitle}
              </span>
              <h3 className="text-lg md:text-xl font-bold text-foreground-900 font-heading mb-4 md:mb-5">
                {integratedBusinessData.dx.title}
              </h3>
              <ul className="space-y-2.5 mb-5 md:mb-6">
                {integratedBusinessData.dx.items.map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2.5 text-sm text-foreground-700">
                    <i className="ri-checkbox-blank-circle-fill text-[6px] text-primary-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => handleClick(integratedBusinessData.dx.linkHref)}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
              >
                {integratedBusinessData.dx.linkLabel}
                <i className="ri-arrow-right-line text-xs" />
              </button>
            </div>
          </div>

          {/* Bottom message - large, centered */}
          <div className="text-center">
            <div className="inline-block py-5 md:py-7 px-6 md:px-10">
              <p className="text-sm md:text-base text-foreground-800 font-medium leading-[2] font-heading">
                {integratedBusinessData.bottomMessage}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}